import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  getMonth,
  getYear,
  startOfMonth,
  startOfWeek,
  addDays,
  isSameDay,
  isAfter as fnsIsAfter,
  isBefore as fnsIsBefore,
  getDate as getFnsDate,
  format,
} from 'date-fns';
import CalendarFill from './icons/CalendarFill';
import {
  PickerContainer,
  TriggerInput,
  CalendarIcon,
  TriggerText,
  PickerWidget,
  CalendarSection,
  DayCell,
  CellHighlight,
  DayText,
  WeekRow,
  TimeBlock,
  SelectedDateText,
} from './DateRangeTimePicker.styles';
import TimeSelector from './DateRangeTimePickerTimeSelector/DateRangeTimePickerTimeSelector';
import MonthNavigation from './DateRangeTimePickerMonthNavigation/DateRangeTimePickerMonthNavigation';
import ActionButtons from './DateRangeTimePickerActionButtons/DateRangeTimePickerActionButtons';
import {
  parseTime,
  convertTo24Hour,
  isDefaultTimeRange,
  formatTimeDisplay,
  formatTimeRangeDisplay,
  formatDateRange,
  formatSingleDate,
} from './helpers';

const DateRangeTimePicker = ({ startDate, endDate, startTime, endTime, onDatesChange }) => {
  const now = new Date();
  const containerRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(getMonth(now));
  const [currentYear, setCurrentYear] = useState(getYear(now));
  const [hoveredDate, setHoveredDate] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(startDate ? new Date(startDate) : null);
  const [tempEndDate, setTempEndDate] = useState(endDate ? new Date(endDate) : null);
  const [tempStartTime, setTempStartTime] = useState(startTime || '00:00');
  const [tempEndTime, setTempEndTime] = useState(endTime || '23:59');
  const [hasChanges, setHasChanges] = useState(false);

  const resetTempState = useCallback(() => {
    setTempStartDate(startDate ? new Date(startDate) : null);
    setTempEndDate(endDate ? new Date(endDate) : null);
    setTempStartTime(startTime || '00:00');
    setTempEndTime(endTime || '23:59');
    setHasChanges(false);
  }, [startDate, endDate, startTime, endTime]);

  useEffect(() => {
    resetTempState();
  }, [resetTempState]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        resetTempState();
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, resetTempState]);

  const handlePrevMonth = useCallback(() => {
    setCurrentMonth((prev) => {
      if (prev === 0) {
        setCurrentYear((y) => y - 1);

        return 11;
      }

      return prev - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth((prev) => {
      if (prev === 11) {
        setCurrentYear((y) => y + 1);

        return 0;
      }

      return prev + 1;
    });
  }, []);

  const handleDateClick = useCallback(
    (date) => {
      // NOTE(vlad|SPD-14091|2026-02-05): Navigate to the clicked date's month if it's not the current month
      const clickedMonth = getMonth(date);
      const clickedYear = getYear(date);
      if (clickedMonth !== currentMonth || clickedYear !== currentYear) {
        setCurrentMonth(clickedMonth);
        setCurrentYear(clickedYear);
      }

      if (!tempStartDate || (tempStartDate && tempEndDate)) {
        setTempStartDate(date);
        setTempEndDate(null);
        setHasChanges(true);
      } else if (fnsIsBefore(date, tempStartDate)) {
        // NOTE(vlad|SPD-14091|2026-02-05): Allow reverse selection - clicking earlier date after start
        // swaps them to maintain intuitive date range selection
        setTempStartDate(date);
        setTempEndDate(tempStartDate);
        setHasChanges(true);
      } else {
        setTempEndDate(date);
        setHasChanges(true);
      }
    },
    [tempStartDate, tempEndDate, currentMonth, currentYear]
  );

  const handleDateHover = useCallback((date) => {
    setHoveredDate(date);
  }, []);

  const handleClearHover = useCallback(() => {
    setHoveredDate(null);
  }, []);

  const handleStartTimeChange = useCallback((hour, minute, ampm) => {
    const time24 = convertTo24Hour(hour, minute, ampm);
    setTempStartTime(time24);
    setHasChanges(true);
  }, []);

  const handleEndTimeChange = useCallback((hour, minute, ampm) => {
    const time24 = convertTo24Hour(hour, minute, ampm);
    setTempEndTime(time24);
    setHasChanges(true);
  }, []);

  const handleClear = useCallback(() => {
    setTempStartDate(null);
    setTempEndDate(null);
    setTempStartTime('00:00');
    setTempEndTime('23:59');
    setHasChanges(false);
    onDatesChange({
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    });
    setIsOpen(false);
  }, [onDatesChange]);

  const handleApply = useCallback(() => {
    let finalStartDate = tempStartDate ? new Date(tempStartDate) : null;
    let finalEndDate = tempEndDate ? new Date(tempEndDate) : null;

    // NOTE(vlad|SPD-14824|2026-05-12): One date click = same-day range. Without this, picking
    // a single date plus an end time silently drops the end time (filter becomes open-ended `gte`).
    // Clone instead of aliasing so a downstream in-place mutation on endDate cannot shift startDate.
    if (finalStartDate && !finalEndDate) {
      finalEndDate = new Date(finalStartDate);
    }

    // NOTE(vlad|SPD-14091|2026-02-05): If same day selected, check if end time is before start time
    if (finalStartDate && finalEndDate && isSameDay(finalStartDate, finalEndDate)) {
      const [startHour, startMinute] = tempStartTime.split(':').map(Number);
      const [endHour, endMinute] = tempEndTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;

      if (endMinutes < startMinutes) {
        finalEndDate = addDays(new Date(finalEndDate), 1);
      }
    }

    onDatesChange({
      startDate: finalStartDate,
      endDate: finalEndDate,
      startTime: tempStartTime,
      endTime: tempEndTime,
    });
    setHasChanges(false);
    setIsOpen(false);
  }, [tempStartDate, tempEndDate, tempStartTime, tempEndTime, onDatesChange]);

  const getCellState = useCallback(
    (date) => {
      const isCurrentMonth = getMonth(date) === currentMonth && getYear(date) === currentYear;
      const isToday = isSameDay(date, new Date());
      const isStart = tempStartDate && isSameDay(date, tempStartDate);
      const isEnd = tempEndDate && isSameDay(date, tempEndDate);
      const isSingleDate = tempStartDate && tempEndDate && isSameDay(tempStartDate, tempEndDate) && isStart;
      const isInRange =
        tempStartDate && tempEndDate && fnsIsAfter(date, tempStartDate) && fnsIsBefore(date, tempEndDate);

      // NOTE(vlad|SPD-14091|2026-02-05): Handle hover preview for both forward and backward selection
      const isHoveringBackward =
        tempStartDate && !tempEndDate && hoveredDate && fnsIsBefore(hoveredDate, tempStartDate);
      const isHoverEnd = hoveredDate && isSameDay(date, hoveredDate) && tempStartDate && !tempEndDate;
      const isHoverForward =
        tempStartDate &&
        !tempEndDate &&
        hoveredDate &&
        !isHoveringBackward &&
        fnsIsAfter(date, tempStartDate) &&
        fnsIsBefore(date, hoveredDate);
      const isHoverBackward =
        tempStartDate &&
        !tempEndDate &&
        hoveredDate &&
        isHoveringBackward &&
        fnsIsBefore(date, tempStartDate) &&
        fnsIsAfter(date, hoveredDate);

      // NOTE(vlad|SPD-14091|2026-02-05): When hovering backward, treat hovered date as visual start and tempStartDate as visual end
      const visualStart = isHoveringBackward ? isHoverEnd : isStart;
      const visualEnd = isHoveringBackward ? isStart : isEnd || isHoverEnd;

      return {
        isCurrentMonth,
        isToday,
        isStart: isSingleDate ? true : visualStart && !visualEnd,
        isEnd: isSingleDate ? true : visualEnd && !visualStart,
        isInRange: isInRange || isHoverForward || isHoverBackward,
        isHoverEnd,
      };
    },
    [currentMonth, currentYear, tempStartDate, tempEndDate, hoveredDate]
  );

  const renderCalendar = useCallback(() => {
    const startOfMonthDate = startOfMonth(new Date(currentYear, currentMonth));
    const startOfCalendar = startOfWeek(startOfMonthDate, { weekStartsOn: 0 });

    const weeks = [];
    let days = [];
    let day = new Date(startOfCalendar);
    for (let i = 0; i < 42; i++) {
      const dateClone = new Date(day);
      const cellState = getCellState(dateClone);

      days.push(
        <DayCell
          key={i}
          onClick={() => handleDateClick(dateClone)}
          onMouseEnter={() => handleDateHover(dateClone)}
          onMouseLeave={handleClearHover}
          isStart={cellState.isStart}
          isEnd={cellState.isEnd}
          isInRange={cellState.isInRange}
          isHoverEnd={cellState.isHoverEnd}
        >
          <CellHighlight
            isStart={cellState.isStart}
            isEnd={cellState.isEnd}
            isInRange={cellState.isInRange}
            isHoverEnd={cellState.isHoverEnd && cellState.isCurrentMonth}
            isToday={cellState.isToday && !cellState.isStart && !cellState.isEnd && !cellState.isInRange}
          >
            <DayText
              isDisabled={!cellState.isCurrentMonth}
              isSelected={cellState.isStart || cellState.isEnd || cellState.isHoverEnd}
            >
              {getFnsDate(dateClone)}
            </DayText>
          </CellHighlight>
        </DayCell>
      );

      if (days.length === 7) {
        weeks.push(<WeekRow key={weeks.length}>{days}</WeekRow>);
        days = [];
      }

      day = addDays(day, 1);
    }

    return weeks;
  }, [currentMonth, currentYear, getCellState, handleDateClick, handleDateHover, handleClearHover]);

  const renderSelectedDateText = useCallback(() => {
    if (!tempStartDate) {
      return null;
    }

    if (!tempEndDate || isSameDay(tempStartDate, tempEndDate)) {
      const dateStr = formatSingleDate(tempStartDate);

      return (
        <>
          <span className="label">Selected: </span>
          {dateStr}
        </>
      );
    }

    const dateStr = formatDateRange(tempStartDate, tempEndDate);
    const hasTimeSet = tempStartTime && tempEndTime && !isDefaultTimeRange(tempStartTime, tempEndTime);
    const timeStr = hasTimeSet ? `, ${formatTimeRangeDisplay(tempStartTime, tempEndTime)}` : '';

    return (
      <>
        <span className="label">Selected: </span>
        {dateStr}
        {timeStr}
      </>
    );
  }, [tempStartDate, tempEndDate, tempStartTime, tempEndTime]);

  const getDisplayText = useCallback(() => {
    const hasTimeSet = tempStartTime && tempEndTime && !isDefaultTimeRange(tempStartTime, tempEndTime);

    if (tempStartDate && tempEndDate) {
      const startStr = format(tempStartDate, 'MMM d, yyyy');
      const endStr = format(tempEndDate, 'MMM d, yyyy');
      const timeStr = hasTimeSet ? ` ${formatTimeRangeDisplay(tempStartTime, tempEndTime)}` : '';

      return `${startStr} - ${endStr}${timeStr}`;
    } else if (tempStartDate) {
      const timeStr = hasTimeSet ? ` ${formatTimeDisplay(tempStartTime)}` : '';

      return `${format(tempStartDate, 'MMM d, yyyy')}${timeStr}`;
    }

    return 'Select date & time';
  }, [tempStartDate, tempEndDate, tempStartTime, tempEndTime]);

  // NOTE(vlad|SPD-14091|2026-02-05): Show action buttons when there are unsaved changes or existing data
  const hasExistingData = startDate || endDate;
  const showButtons = hasChanges || hasExistingData;

  const startTimeParsed = parseTime(tempStartTime);
  const endTimeParsed = parseTime(tempEndTime);

  return (
    <PickerContainer ref={containerRef}>
      <TriggerInput
        onClick={() => {
          if (isOpen) {
            resetTempState();
          }
          setIsOpen(!isOpen);
        }}
        $isPlaceholder={!tempStartDate}
        $isOpen={isOpen}
      >
        <CalendarIcon>
          <CalendarFill size={14} />
        </CalendarIcon>
        <TriggerText $isPlaceholder={!tempStartDate}>{getDisplayText()}</TriggerText>
      </TriggerInput>

      {isOpen && (
        <PickerWidget>
          <CalendarSection>
            <MonthNavigation
              currentMonth={currentMonth}
              currentYear={currentYear}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
            />

            {renderCalendar()}
          </CalendarSection>

          <TimeBlock>
            <TimeSelector
              label="Start time:"
              hour={startTimeParsed.hour12}
              minute={startTimeParsed.minute}
              ampm={startTimeParsed.ampm}
              onHourChange={(hour) => handleStartTimeChange(hour, startTimeParsed.minute, startTimeParsed.ampm)}
              onMinuteChange={(minute) => handleStartTimeChange(startTimeParsed.hour12, minute, startTimeParsed.ampm)}
              onAmPmChange={(ampm) => handleStartTimeChange(startTimeParsed.hour12, startTimeParsed.minute, ampm)}
            />

            <TimeSelector
              label="End time:"
              hour={endTimeParsed.hour12}
              minute={endTimeParsed.minute}
              ampm={endTimeParsed.ampm}
              onHourChange={(hour) => handleEndTimeChange(hour, endTimeParsed.minute, endTimeParsed.ampm)}
              onMinuteChange={(minute) => handleEndTimeChange(endTimeParsed.hour12, minute, endTimeParsed.ampm)}
              onAmPmChange={(ampm) => handleEndTimeChange(endTimeParsed.hour12, endTimeParsed.minute, ampm)}
            />
          </TimeBlock>

          {showButtons && (
            <ActionButtons onClear={handleClear} onApply={handleApply}>
              <SelectedDateText>{renderSelectedDateText()}</SelectedDateText>
            </ActionButtons>
          )}
        </PickerWidget>
      )}
    </PickerContainer>
  );
};

DateRangeTimePicker.propTypes = {
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  onDatesChange: PropTypes.func.isRequired,
};

export default DateRangeTimePicker;
